// HomePage — post-login module selector (`/`).
//
// Renders inside <BlankShell />, so this page owns the entire viewport.
// Layout:
//   - Tricivo wordmark heading.
//   - Subtitle "Select a module to continue".
//   - Primary grid — full-size module cards (Collections / Skip / Weighbridge).
//   - Corner cubes — small fixed cubes in bottom-right (Reports / Settings / Kiosk).
//
// Primary modules render in a centred grid. Secondary modules (secondary: true)
// appear as compact 72×72px cubes stacked in the bottom-right corner of the
// viewport. Hidden on mobile (≤720px).

import type { ComponentType } from 'react';
import type { LucideProps } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Container, Scale, Settings, Truck } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import styles from './HomePage.module.css';

type ModuleStatus = 'active' | 'coming_soon';

interface ModuleCard {
  key: string;
  title: string;
  description: string;
  icon: ComponentType<LucideProps>;
  status: ModuleStatus;
  to: string;
  // When true the card renders in the secondary (half-height) row. Falsy =
  // a full-size primary card. Drives the two-tier homepage split.
  secondary?: boolean;
  // When true the card is only shown to admin-role users.
  adminOnly?: boolean;
}

const MODULES: ModuleCard[] = [
  {
    key: 'collections',
    title: 'Collections',
    description:
      'Manage vehicle collection bookings, receipts, customers and drivers.',
    icon: Truck,
    status: 'active',
    to: '/collections/dashboard',
  },
  {
    key: 'skip',
    title: 'Skip Management',
    description: 'Manage skip hire jobs, enquiries, inventory and customers.',
    icon: Container,
    status: 'active',
    to: '/skip/dashboard',
  },
  {
    key: 'weighbridge',
    title: 'Weighbridge Management',
    description: 'Manage weighbridge sites, agents and weighbridge tickets.',
    icon: Scale,
    status: 'active',
    to: '/weighbridge/dashboard',
  },
  {
    key: 'reports',
    title: 'Reports',
    description:
      'Operational insights, summaries, and exports across every module.',
    icon: BarChart3,
    status: 'active',
    to: '/reports',
    secondary: true,
  },
  {
    key: 'settings',
    title: 'Settings',
    description: 'Company profile, reference data, notifications, and templates.',
    icon: Settings,
    status: 'active',
    to: '/settings',
    secondary: true,
    adminOnly: true,
  },
  // Kiosk cube — links to the touch weighbridge kiosk. Visible to every role
  // that reaches this page (a weighbridge_operator is bounced straight to the
  // kiosk by ProtectedRoute and never lands here).
  {
    key: 'kiosk',
    title: 'Kiosk',
    description: 'Open the touch weighbridge kiosk for this device.',
    icon: Scale,
    status: 'active',
    to: '/weighbridge/kiosk',
    secondary: true,
  },
];

export function HomePage(): JSX.Element {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const isOperator = role === 'weighbridge_operator';
  const isAdmin = role === 'admin';

  // A weighbridge_operator can only use the kiosk — hide every office module,
  // leaving just the Kiosk cube. (In practice ProtectedRoute redirects them
  // before this page renders; the guard keeps the component self-consistent.)
  const visibleModules = isOperator
    ? MODULES.filter((mod) => mod.key === 'kiosk')
    : MODULES.filter((mod) => !mod.adminOnly || isAdmin);
  const primaryModules = visibleModules.filter((mod) => !mod.secondary);
  const secondaryModules = visibleModules.filter((mod) => mod.secondary);

  const renderCard = (mod: ModuleCard): JSX.Element => {
    const isActive = mod.status === 'active';
    return (
      <li key={mod.key}>
        <button
          type="button"
          className={`${styles.card} ${isActive ? styles.cardActive : styles.cardMuted}`}
          onClick={() => navigate(mod.to)}
          aria-label={`${mod.title} — ${isActive ? 'Active' : 'Coming Soon'}`}
        >
          <span
            className={`${styles.badge} ${isActive ? styles.badgeActive : styles.badgeComingSoon}`}
          >
            {isActive ? 'Active' : 'Coming Soon'}
          </span>
          <span className={styles.iconWrap} aria-hidden>
            <mod.icon size={28} />
          </span>
          <span className={styles.text}>
            <span className={styles.title}>{mod.title}</span>
            <span className={styles.description}>{mod.description}</span>
          </span>
        </button>
      </li>
    );
  };

  const renderCube = (mod: ModuleCard): JSX.Element => (
    <button
      key={mod.key}
      type="button"
      className={styles.cube}
      onClick={() => navigate(mod.to)}
      aria-label={mod.title}
    >
      <mod.icon size={20} aria-hidden />
      <span className={styles.cubeLabel}>{mod.title}</span>
    </button>
  );

  return (
    <div className={styles.page}>
      <div className={styles.center}>
        <header className={styles.brand}>
          <h1 className={styles.wordmark}>Tricivo</h1>
          <p className={styles.subtitle}>Select a module to continue</p>
        </header>

        <div className={styles.modules}>
          <ul className={`${styles.grid} ${styles.gridThree}`}>
            {primaryModules.map(renderCard)}
          </ul>
        </div>
      </div>

      {secondaryModules.length > 0 && (
        <div className={styles.cornerCubes}>
          {secondaryModules.map(renderCube)}
        </div>
      )}
    </div>
  );
}
